import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Hls from 'hls.js';
import { fetchDramaCategories, fetchDramaList, recordDistributorShareBrowse, recordHomeVisit } from '@/api/member';
import { mapDramaCard, type CinemaSeries } from '@/utils/cinemaData';
import { readReferralCode } from '@/utils/memberSession';
import CreatorCenterBanner from '../components/CreatorCenterBanner';
import RankingPeriodTabs, { rankingViews, type RankingPeriod } from '../components/RankingPeriodTabs';

interface CategoryChip {
  id: string;
  label: string;
  categoryId: number;
  code: string;
}

const INITIAL_SERIES_COUNT = 12;
const SERIES_LOAD_STEP = 6;

function timeValue(value?: string) {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export default function CinemaHomePage() {
  useEffect(() => { void recordHomeVisit().catch(() => undefined); }, []);
  const { t, i18n } = useTranslation('cinema');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroImageReady, setHeroImageReady] = useState(false);
  const [seriesRows, setSeriesRows] = useState<CinemaSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryRows, setCategoryRows] = useState<CategoryChip[]>([{ id: 'all', label: t('cinema_categoryAll'), categoryId: 0, code: '' }]);
  const [visibleSeriesCount, setVisibleSeriesCount] = useState(INITIAL_SERIES_COUNT);
  const [rankingPeriod, setRankingPeriod] = useState<RankingPeriod>('total');
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Read URL params
  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setActiveCategory(cat);
    const q = searchParams.get('search');
    if (q) setSearchQuery(q);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    void fetchDramaList()
      .then((rows) => {
        if (cancelled) return;
        setSeriesRows(rows.map(mapDramaCard));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    void fetchDramaCategories()
      .then((categories) => {
        if (!cancelled && categories.length) {
          setCategoryRows([
            { id: 'all', label: t('cinema_categoryAll'), categoryId: 0, code: '' },
            ...categories.map((item) => ({
              id: item.code || String(item.id),
              label: item.name,
              categoryId: Number(item.id) || 0,
              code: item.code || '',
            })),
          ]);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [i18n.resolvedLanguage, t]);

  // The hero is curated explicitly in the admin panel.
  const featuredSeries = useMemo(() => {
    return seriesRows.filter((series) => series.isCarousel);
  }, [seriesRows]);

  // Start the display timer only after the current hero image is visible.
  useEffect(() => {
    if (featuredSeries.length <= 1 || !heroImageReady) return;
    const timer = window.setTimeout(() => {
      setHeroImageReady(false);
      setHeroIndex((prev) => (prev + 1) % featuredSeries.length);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [featuredSeries.length, heroImageReady, heroIndex]);

  useEffect(() => {
    setHeroImageReady(false);
    setHeroIndex(0);
  }, [seriesRows]);

  const currentHero = featuredSeries[heroIndex];

  useEffect(() => {
    setHeroImageReady(false);
  }, [currentHero?.carouselImageUrl, currentHero?.coverUrl]);

  useEffect(() => {
    if (!heroImageReady || featuredSeries.length <= 1) return;
    const nextHero = featuredSeries[(heroIndex + 1) % featuredSeries.length];
    const nextHeroImage = nextHero?.carouselImageUrl || nextHero?.coverUrl;
    if (!nextHeroImage) return;
    const preload = new Image();
    preload.fetchPriority = 'high';
    preload.src = nextHeroImage;
  }, [featuredSeries, heroImageReady, heroIndex]);

  // Filtered series
  const filteredSeries = useMemo(() => {
    const selectedCategory = categoryRows.find((cat) => cat.id === activeCategory);
    let result = activeCategory === 'all' || !selectedCategory
      ? [...seriesRows]
      : seriesRows.filter((s) => {
          if (selectedCategory.categoryId > 0) return s.categoryId === selectedCategory.categoryId;
          if (selectedCategory.code) return s.categoryCode === selectedCategory.code;
          return s.category === selectedCategory.label;
        });
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q)
      );
    }
    return result.sort((a, b) => rankingViews(b, rankingPeriod) - rankingViews(a, rankingPeriod) || b.views - a.views || Number(b.id) - Number(a.id));
  }, [activeCategory, categoryRows, rankingPeriod, searchQuery, seriesRows]);

  const visibleSeries = useMemo(
    () => filteredSeries.slice(0, visibleSeriesCount),
    [filteredSeries, visibleSeriesCount],
  );
  const hasMoreSeries = visibleSeries.length < filteredSeries.length;

  useEffect(() => {
    setVisibleSeriesCount(INITIAL_SERIES_COUNT);
  }, [activeCategory, rankingPeriod, searchQuery]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMoreSeries || loading) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisibleSeriesCount((current) => Math.min(current + SERIES_LOAD_STEP, filteredSeries.length));
      },
      { rootMargin: '160px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [filteredSeries.length, hasMoreSeries, loading]);

  const hotSeries = useMemo(() => {
    return seriesRows
      .filter((series) => series.isHot)
      .sort((a, b) => a.hotRank - b.hotRank)
      .slice(0, 8);
  }, [seriesRows]);
  const newSeries = useMemo(() => {
    return seriesRows
      .filter((series) => series.sourceType === 'platform')
      .sort((a, b) => timeValue(b.latestEpisodeAt || b.createdAt) - timeValue(a.latestEpisodeAt || a.createdAt) || Number(b.id) - Number(a.id))
      .slice(0, 8);
  }, [seriesRows]);

  const handleSeriesClick = useCallback((series: CinemaSeries) => {
    const shareCode = readReferralCode();
    if (shareCode) {
      void recordDistributorShareBrowse({
        shareCode,
        dramaId: Number(series.id) || 0,
        channel: 'web',
      }).catch(() => undefined);
    }
    navigate(`/watch/${series.id}`);
  }, [navigate]);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[620px] overflow-hidden bg-background-900">
        {currentHero && (
          <>
          {/* Background image */}
          <div className="absolute inset-0">
            <ProgressiveCover
              src={currentHero.carouselImageUrl || currentHero.coverUrl}
              alt={currentHero.title}
              className="w-full h-full object-cover object-center"
              priority
              onLoadStateChange={setHeroImageReady}
            />
          </div>

          {/* Dark gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/40 to-transparent z-[5]" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/60 via-transparent to-transparent z-[5]" />

          {/* Hero content */}
          <div className={`relative z-10 h-full flex items-end md:items-center pb-10 md:pb-0 transition-opacity duration-500 ${heroImageReady ? 'opacity-100' : 'pointer-events-none opacity-0'}`}>
            <div className="px-4 md:px-10 lg:px-14 w-full max-w-2xl">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                {currentHero.tagNew && (
                  <span className="px-2 py-0.5 rounded text-[11px] font-bold text-background-950 bg-[#FB923C] whitespace-nowrap">
                    {t('cinema_newLabel')}
                  </span>
                )}
                {currentHero.tagHot && (
                  <span className="px-2 py-0.5 rounded text-[11px] font-bold text-background-950 bg-accent-400 whitespace-nowrap">
                    {t('cinema_hotLabel')}
                  </span>
                )}
                <span className="px-2 py-0.5 rounded text-[11px] font-medium text-background-50 bg-foreground-50/8 whitespace-nowrap">
                  {currentHero.category}
                </span>
              </div>

              <h1 className="text-2xl md:text-5xl font-bold font-heading text-foreground-50 leading-tight mb-2 md:mb-3">
                {currentHero.title}
              </h1>

              <p className="text-sm md:text-base text-background-50/80 leading-relaxed line-clamp-2 md:line-clamp-3 mb-4 md:mb-6">
                {currentHero.description}
              </p>

              <div className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-background-50/90 mb-4 md:mb-6 flex-wrap">
                {currentHero.rating > 0 && (
                  <>
                    <span className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-xs"></i>
                      </span>
                      {t('cinema_rating', { score: currentHero.rating })}
                    </span>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-600" />
                  </>
                )}
                <span>{t('cinema_episodes', { count: currentHero.totalEpisodes })}</span>
                {currentHero.views > 0 && (
                  <>
                    <span className="w-0.5 h-0.5 rounded-full bg-foreground-600 hidden sm:inline" />
                    <span className="hidden sm:inline">
                      {t('cinema_views', { count: currentHero.views >= 10000 ? `${(currentHero.views / 10000).toFixed(1)}万` : currentHero.views })}
                    </span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 md:gap-3">
                <button
                  onClick={() => handleSeriesClick(currentHero)}
                  className="flex items-center gap-1.5 md:gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-full text-sm font-semibold text-background-950 bg-[#F19744] hover:bg-[#F19744]/90 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-play-fill text-lg"></i>
                  </span>
                  {t('cinema_watchNow')}
                </button>
                <button
                  onClick={() => handleSeriesClick(currentHero)}
                  className="flex items-center gap-1.5 md:gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-full text-sm font-medium text-background-50 bg-foreground-50/8 hover:bg-foreground-50/14 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-information-line"></i>
                  </span>
                  {t('cinema_heroMore')}
                </button>
              </div>
            </div>
          </div>

          {/* Hero dots */}
          {featuredSeries.length > 1 && (
            <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
              {featuredSeries.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setHeroImageReady(false);
                    setHeroIndex(idx);
                  }}
                  className={`h-1 rounded-full transition-all cursor-pointer ${
                    idx === heroIndex
                      ? 'w-6 bg-[#F19744]'
                      : 'w-2 bg-foreground-50/30 hover:bg-foreground-50/50'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Side thumbnail strip — desktop only */}
          <div className="absolute right-6 md:right-10 lg:right-14 top-1/2 -translate-y-1/2 z-20 hidden lg:flex flex-col gap-3">
            {featuredSeries.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => {
                  setHeroImageReady(false);
                  setHeroIndex(idx);
                }}
                className={`relative w-28 h-16 rounded-lg overflow-hidden transition-all cursor-pointer ${
                  idx === heroIndex
                    ? 'ring-2 ring-[#F19744] scale-105'
                    : 'opacity-50 hover:opacity-80'
                }`}
              >
                <ProgressiveCover
                  src={s.carouselImageUrl || s.coverUrl}
                  alt={s.title}
                  className="w-full h-full object-cover"
                  priority={idx < 2}
                />
              </button>
            ))}
          </div>
          </>
        )}

        <div
          aria-live="polite"
          className={`absolute inset-0 z-30 flex items-center justify-center overflow-hidden bg-background-900 transition-opacity duration-500 ${currentHero && heroImageReady ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
        >
          <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-background-800 via-background-900 to-background-950" />
          <div className="relative flex flex-col items-center gap-3 text-foreground-400">
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-background-700 bg-background-950/70">
              <i className="ri-loader-4-line animate-spin text-2xl text-accent-500" />
            </span>
            <span className="text-sm font-medium">{t('cinema_carouselLoading')}</span>
          </div>
        </div>
      </section>

      {/* Main content */}
      <div className="relative z-10 space-y-8 md:space-y-10 pb-16 md:pb-20">
        <CreatorCenterBanner desktopOnly />
        {/* Category filter */}
        <div className="px-4 md:px-10 lg:px-14 pt-4 md:pt-6">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
            {categoryRows.map((cat) => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setSearchQuery(''); }}
                className={`flex-shrink-0 whitespace-nowrap px-4 md:px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  activeCategory === cat.id
                    ? 'bg-[#F19744] text-background-950'
                    : 'bg-foreground-50/6 text-foreground-400 hover:text-foreground-200 hover:bg-foreground-50/10'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Hot Recommend Row */}
        {activeCategory === 'all' && !searchQuery && hotSeries.length > 0 && (
          <section className="px-4 md:px-10 lg:px-14">
            <div className="flex items-center justify-between mb-4 md:mb-5">
              <h2 className="text-base md:text-lg font-bold font-heading text-foreground-100">{t('cinema_hotRecommend')}</h2>
            </div>
            <SeriesRow
              series={hotSeries}
              onClick={handleSeriesClick}
              t={t}
              prioritizeCovers={heroImageReady}
              label="hot"
            />
          </section>
        )}

        {/* New Release Row */}
        {activeCategory === 'all' && !searchQuery && newSeries.length > 0 && (
          <section className="px-4 md:px-10 lg:px-14">
            <div className="flex items-center justify-between mb-4 md:mb-5">
              <h2 className="text-base md:text-lg font-bold font-heading text-foreground-100">{t('cinema_newRelease')}</h2>
            </div>
            <SeriesRow
              series={newSeries}
              onClick={handleSeriesClick}
              t={t}
              prioritizeCovers={heroImageReady}
              label="new"
            />
          </section>
        )}

        {/* All / Filtered Grid */}
        <section className="px-4 md:px-10 lg:px-14">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 md:mb-5">
            <h2 className="text-base md:text-lg font-bold font-heading text-foreground-100">
              {searchQuery ? `"${searchQuery}"` : t('cinema_allSeries')}
            </h2>
            <div className="flex items-center">
              <RankingPeriodTabs
                value={rankingPeriod}
                onChange={setRankingPeriod}
                labels={{
                  total: t('cinema_rankingTotal'),
                  month: t('cinema_rankingMonth'),
                  week: t('cinema_rankingWeek'),
                  latest: t('cinema_rankingLatest'),
                  creator: t('cinema_rankingCreator'),
                }}
              />
            </div>
          </div>

          {filteredSeries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-foreground-600">
              <span className="w-16 h-16 flex items-center justify-center mb-4 rounded-full bg-foreground-50/5">
                <i className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-film-line'} text-3xl`}></i>
              </span>
              <p className="text-sm">{loading ? '加载中...' : searchQuery ? t('cinema_noResults') : t('cinema_noSeries')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
              {visibleSeries.map((series, index) => (
                <SeriesCard
                  key={series.id}
                  series={series}
                  onClick={() => handleSeriesClick(series)}
                  t={t}
                  priority={index < 4 && activeCategory !== 'all'}
                />
              ))}
            </div>
          )}
          {hasMoreSeries && (
            <div ref={loadMoreRef} className="flex h-12 items-center justify-center" aria-label="加载更多">
              <i className="ri-loader-4-line animate-spin text-lg text-foreground-600" aria-hidden="true"></i>
            </div>
          )}
        </section>
      </div>

    </div>
  );
}

// ---- Sub-components ----

function SeriesRow({
  series,
  onClick,
  t,
  prioritizeCovers = false,
  label = "none",
}: {
  series: CinemaSeries[];
  onClick: (s: CinemaSeries) => void;
  t: (key: string, opts?: Record<string, string | number>) => string;
  prioritizeCovers?: boolean;
  label?: "new" | "hot" | "none";
}) {
  return (
    <div className="flex gap-2 md:gap-3 overflow-x-auto scrollbar-hide pb-2">
      {series.map((s, index) => (
        <div key={s.id} className="flex-shrink-0 w-36 md:w-44">
          <SeriesCard
            series={s}
            onClick={() => onClick(s)}
            t={t}
            priority={prioritizeCovers && index < 4}
            label={label}
          />
        </div>
      ))}
    </div>
  );
}

function ProgressiveCover({
  src,
  alt,
  className,
  priority = false,
  hidden = false,
  onLoadStateChange,
}: {
  src: string;
  alt: string;
  className: string;
  priority?: boolean;
  hidden?: boolean;
  onLoadStateChange?: (loaded: boolean) => void;
}) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [shouldLoad, setShouldLoad] = useState(priority);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const retryTimerRef = useRef<number | null>(null);
  const onLoadStateChangeRef = useRef(onLoadStateChange);

  useEffect(() => {
    onLoadStateChangeRef.current = onLoadStateChange;
  }, [onLoadStateChange]);

  useEffect(() => {
    setStatus(src ? 'loading' : 'error');
    setRetryAttempt(0);
    onLoadStateChangeRef.current?.(false);
  }, [src]);

  useEffect(() => {
    if (priority) {
      setShouldLoad(true);
      return;
    }

    const target = containerRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: '700px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [priority]);

  useEffect(() => () => {
    if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
  }, []);

  const retrySrc = useMemo(() => {
    if (!src || retryAttempt === 0) return src;
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('_cover_retry', String(retryAttempt));
      return url.toString();
    } catch {
      return src;
    }
  }, [retryAttempt, src]);

  const handleLoaded = useCallback(() => {
    if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
    setStatus('loaded');
    onLoadStateChangeRef.current?.(true);
  }, []);

  useEffect(() => {
    const image = imageRef.current;
    if (!shouldLoad || !image?.complete || image.naturalWidth <= 0) return;
    handleLoaded();
  }, [handleLoaded, retrySrc, shouldLoad]);

  const handleError = () => {
    setStatus('error');
    onLoadStateChangeRef.current?.(false);
    if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    const retryDelay = retryAttempt < 3 ? 600 * (retryAttempt + 1) : 30000;
    retryTimerRef.current = window.setTimeout(() => {
      setStatus('loading');
      setRetryAttempt((attempt) => attempt + 1);
      retryTimerRef.current = null;
    }, retryDelay);
  };

  return (
    <div ref={containerRef} className="absolute inset-0">
      <div
        aria-hidden="true"
        className={`absolute inset-0 flex items-center justify-center bg-background-800 transition-opacity duration-300 ${
          status === 'loaded' ? 'pointer-events-none opacity-0' : 'opacity-100'
        } ${status === 'loading' ? 'animate-pulse' : ''}`}
      >
        <i className="ri-image-line text-2xl text-foreground-700" />
      </div>
      {src && shouldLoad && (
        <img
          key={retrySrc}
          ref={(image) => {
            imageRef.current = image;
            if (image?.complete && image.naturalWidth > 0) handleLoaded();
          }}
          src={retrySrc}
          alt={alt}
          loading="eager"
          fetchPriority={priority ? 'high' : 'auto'}
          decoding="async"
          onLoad={handleLoaded}
          onError={handleError}
          className={`${className} transition-opacity duration-300 ${
            status === 'loaded' && !hidden ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </div>
  );
}

function SeriesHoverPreview({ videoUrl, poster }: { videoUrl: string; poster: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const play = () => void video.play().catch(() => undefined);
    let hls: Hls | null = null;
    const isHlsVideo = /\.m3u8(?:$|[?#])/i.test(videoUrl);

    if (isHlsVideo && Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        autoStartLoad: false,
        startLevel: 0,
        capLevelToPlayerSize: true,
        maxBufferLength: 6,
        maxMaxBufferLength: 10,
        backBufferLength: 0,
      });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls?.loadSource(videoUrl));
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!hls || hls.levels.length === 0) return;
        const lowestLevel = hls.levels.reduce((lowestIndex, level, index, levels) => {
          const lowest = levels[lowestIndex];
          const currentRank = level.bitrate > 0 ? level.bitrate : level.height || Number.MAX_SAFE_INTEGER;
          const lowestRank = lowest.bitrate > 0 ? lowest.bitrate : lowest.height || Number.MAX_SAFE_INTEGER;
          return currentRank < lowestRank ? index : lowestIndex;
        }, 0);
        hls.startLevel = lowestLevel;
        hls.currentLevel = lowestLevel;
        hls.autoLevelCapping = lowestLevel;
        hls.startLoad();
        play();
      });
    } else {
      video.src = videoUrl;
      video.load();
      video.addEventListener('canplay', play, { once: true });
    }

    return () => {
      hls?.destroy();
      video.pause();
      video.removeEventListener('canplay', play);
      video.removeAttribute('src');
      video.load();
    };
  }, [videoUrl]);

  return (
    <video
      ref={videoRef}
      poster={poster}
      className="absolute inset-0 hidden h-full w-full object-cover md:block"
      autoPlay
      muted
      loop
      playsInline
      preload="none"
    />
  );
}

function SeriesCard({
  series,
  onClick,
  t,
  priority = false,
  label = "none",
}: {
  series: CinemaSeries;
  onClick: () => void;
  t: (key: string, opts?: Record<string, string | number>) => string;
  priority?: boolean;
  label?: "new" | "hot" | "none";
}) {
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const previewUrl = series.episodes.find((episode) => !episode.locked && episode.videoUrl)?.videoUrl
    || series.episodes.find((episode) => episode.videoUrl)?.videoUrl
    || "";

  useEffect(() => () => {
    if (hoverTimerRef.current !== null) window.clearTimeout(hoverTimerRef.current);
  }, []);

  const handleMouseEnter = () => {
    if (!previewUrl) return;
    hoverTimerRef.current = window.setTimeout(() => {
      setIsHovered(true);
      hoverTimerRef.current = null;
    }, 180);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setIsHovered(false);
  };

  return (
    <button
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="group w-full text-left bg-background-900/60 rounded-xl overflow-hidden transition-all hover:bg-background-900/90 cursor-pointer"
    >
      {/* Poster */}
      <div className="relative aspect-[2/3] overflow-hidden">
        <ProgressiveCover
          src={series.coverUrl}
          alt={series.title}
          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
          priority={priority}
          hidden={isHovered && Boolean(previewUrl)}
        />
        {isHovered && previewUrl && (
          <SeriesHoverPreview videoUrl={previewUrl} poster={series.coverUrl} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent" />

        {/* Tags */}
        {label !== "none" && (
          <div className="absolute top-2 left-2">
          {label === "new" && (
            <span className="text-[10px] font-bold text-background-950 bg-[#FB923C] px-1.5 py-0.5 rounded whitespace-nowrap">
              {t('cinema_newLabel')}
            </span>
          )}
          {label === "hot" && (
            <span className="text-[10px] font-bold text-background-950 bg-accent-400 px-1.5 py-0.5 rounded whitespace-nowrap">
              {t('cinema_hotLabel')}
            </span>
          )}
          </div>
        )}

        {series.rating > 0 && (
          <div className="absolute top-2 right-2 bg-background-950/70 backdrop-blur-sm rounded-md px-1.5 py-0.5 flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center text-accent-500 flex-shrink-0">
              <i className="ri-star-fill text-[10px]"></i>
            </span>
            <span className="text-[11px] font-semibold text-foreground-200">{series.rating}</span>
          </div>
        )}

        {/* Play overlay on hover */}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-[#F19744] text-background-950 shadow-lg">
            <i className="ri-play-fill text-xl md:text-2xl"></i>
          </span>
        </div>

        {/* Bottom info */}
        <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3">
          <h3 className="text-xs md:text-sm font-semibold text-foreground-100 leading-tight line-clamp-1">
            {series.title}
          </h3>
          <div className="flex items-center gap-1.5 md:gap-2 mt-0.5 md:mt-1 text-[10px] md:text-[11px] text-foreground-500">
            <span>{t('cinema_episodes', { count: series.totalEpisodes })}</span>
            {series.views > 0 && (
              <>
                <span className="w-0.5 h-0.5 rounded-full bg-foreground-700" />
                <span>{series.views >= 10000 ? `${(series.views / 10000).toFixed(1)}万` : series.views}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Category badge */}
      <div className="px-2 md:px-3 py-1.5 md:py-2">
        <span className="text-[10px] text-accent-400 bg-accent-500/10 px-1.5 py-0.5 rounded whitespace-nowrap">
          {series.category}
        </span>
      </div>
    </button>
  );
}
